import React, { useEffect, useState } from 'react';
import ModalComponent from '../../../components/modal/modal';
import ModalBodyComponent from '../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import ViewUploadedDoc from '../../../components/view-uploaded-doc';
import Heading from '../../../components/typography';
import { COLORS } from '../../../constants/style/color';
import {
  TaxBracketAccordingValue,
  TaxBracketHeaders,
  TaxBracketValue,
  TotalContainer,
  TransactionCard,
  TransactionDetail,
  TransactionSingleDetailBlock,
} from '../../exercise/exercise.styled';
import { convertNumberWithCommas } from '../../../utils/global';
import Tabs from '../../../components/tabs';
import { TabContent } from '../../../components/tabs/tabs.styled';
import Tag from '../../../components/lozenge/tags';
import { ACCEPT_DECIMAL } from '../../../utils/apiDecimalConfig';

interface ViewExerciseModalProps {
  isModalOpen: boolean;
  closeModal: () => void;
  exerciseResponse: any;
  row: any;
}

const ViewExerciseModal = ({
  isModalOpen,
  closeModal,
  exerciseResponse,
  row,
}: ViewExerciseModalProps): JSX.Element => {
  const [openTab, setOpenTab] = useState(0);

  const items = [
    {
      id: 'taxDetails',
      name: 'Tax Details',
      content: <TaxDetailsComponent viewData={exerciseResponse} />,
    },
    {
      id: 'transactionDetails',
      name: 'Transaction Details',
      content: <TransactionDetailsComponent exerciseResponse={exerciseResponse} />,
    },
  ];

  return (
    <ModalComponent
      size='xxl'
      show={isModalOpen}
      closeModal={closeModal}
      title='Estimate Exercise Details'
      subTitle='This is an estimate of the exercise detail'
      id='viewExerciseModal'>
      <>
        <div id='modalBody'>
          <ModalBodyComponent subTitle={true}>
            <div id='exerciseEstimateSummary'>
              <ViewUploadedDoc
                className='items-start mb-8'
                variant='primary'
                children={
                  <div
                    className='grid sm:grid-cols-4 w-full gap-x-[42px] gap-y-[24px]'
                    id='estimateSummaryGrid'>
                    <div id='awardNo'>
                      <Heading
                        variant='body-xs'
                        title='Award No'
                        color={COLORS.Gray}
                        id='awardNoLabel'
                      />
                      <Heading
                        variant='body'
                        title={row?.awardNumber || '-'}
                        color={COLORS.Black}
                        id='awardNoValue'
                      />
                    </div>
                    <div id='exercisePrice'>
                      <Heading
                        variant='body-xs'
                        title='Exercise Price'
                        color={COLORS.Gray}
                        id='exercisePriceLabel'
                      />
                      <Heading
                        variant='body'
                        title={
                          exerciseResponse?.transaction?.totalCostPrice === 0
                            ? '$0'
                            : exerciseResponse?.transaction?.totalCostPrice
                            ? '$' +
                              convertNumberWithCommas(
                                Number(exerciseResponse?.transaction.totalCostPrice).toFixed(
                                  ACCEPT_DECIMAL
                                ),
                                true
                              )
                            : '-'
                        }
                        color={COLORS.Black}
                        id='exercisePriceValue'
                      />
                    </div>
                    {/* <div id='commissionsAndFees'>
                      <Heading
                        variant='body-xs'
                        title='Commissions & Fees'
                        color={COLORS.Gray}
                        id='commissionsAndFeesLabel'
                      />
                      <Heading
                        variant='body'
                        title='-'
                        color={COLORS.Black}
                        id='commissionsAndFeesValue'
                      />
                    </div> */}
                    {/* <div id='settlementType'>
                      <Heading
                        variant='body-xs'
                        title='Settlement Type'
                        color={COLORS.Gray}
                        id='settlementTypeLabel'
                      />
                      <Heading
                        variant='body'
                        title={row?.settlementTypeName || '-'}
                        color={COLORS.Black}
                        id='settlementTypeValue'
                      />
                    </div> */}
                    <div id='estimatedSharesToTransfer'>
                      <Heading
                        variant='body-xs'
                        title='Estimated Shares to Transfer'
                        color={COLORS.Gray}
                        id='estimatedSharesToTransferLabel'
                      />
                      <Heading
                        variant='body'
                        title={
                          exerciseResponse?.transaction?.transferSharesDueParticipant === 0
                            ? '0'
                            : exerciseResponse?.transaction?.transferSharesDueParticipant
                            ? String(
                                convertNumberWithCommas(
                                  Number(exerciseResponse.transaction.transferSharesDueParticipant),
                                  false
                                )
                              )
                            : '-'
                        }
                        color={COLORS.Black}
                        id='estimatedSharesToTransferValue'
                      />
                    </div>
                    <div id='estimatedSharesToSell'>
                      <Heading
                        variant='body-xs'
                        title='Estimated Shares to Sell'
                        color={COLORS.Gray}
                        id='estimatedSharesToSellLabel'
                      />
                      <Heading
                        variant='body'
                        title={
                          exerciseResponse?.transaction?.sellOrderShares === 0
                            ? '0'
                            : exerciseResponse?.transaction?.sellOrderShares
                            ? String(
                                convertNumberWithCommas(
                                  Number(exerciseResponse.transaction.sellOrderShares),
                                  false
                                )
                              )
                            : '-'
                        }
                        color={COLORS.Black}
                        id='estimatedSharesToSellValue'
                      />
                    </div>
                    <div id='estimatedNetCash'>
                      <Heading
                        variant='body-xs'
                        title='Estimated Net Cash'
                        color={COLORS.Gray}
                        id='estimatedNetCashLabel'
                      />
                      <Heading
                        variant='body'
                        title={
                          exerciseResponse?.netCash === 0
                            ? '$0.00'
                            : exerciseResponse?.netCash
                            ? '$' +
                              convertNumberWithCommas(
                                Number(exerciseResponse.netCash).toFixed(ACCEPT_DECIMAL),
                                true
                              )
                            : '-'
                        }
                        color={COLORS.Black}
                        id='estimatedNetCashValue'
                      />
                    </div>
                    <div id='estimatedNetShare'>
                      <Heading
                        variant='body-xs'
                        title='Estimated Net Share'
                        color={COLORS.Gray}
                        id='estimatedNetShareLabel'
                      />
                      <Heading
                        variant='body'
                        title={
                          exerciseResponse?.netShare === 0
                            ? '0'
                            : exerciseResponse?.netShare
                            ? String(convertNumberWithCommas(exerciseResponse.netShare, false))
                            : '-'
                        }
                        color={COLORS.Black}
                        id='estimatedNetShareValue'
                      />
                    </div>
                  </div>
                }
              />
            </div>

            <div id='exerciseDetailsTabs'>
              <div id='tabNavigation' style={{ display: 'flex', justifyContent: 'center' }}>
                <Tabs
                  variant='fit-content'
                  items={items}
                  tabIndex={openTab}
                  setTabIndex={(index: number) => {
                    setOpenTab(index);
                  }}
                  id='exerciseViewDetails'
                />
              </div>
              <div id='tabContent'>
                {items.map((item: any, i: number) => (
                  <TabContent key={`tabs-${i}`} id={`tabPanel.${i}`}>
                    {openTab === i ? item.content : ''}
                  </TabContent>
                ))}
              </div>
            </div>
          </ModalBodyComponent>
        </div>
        <div id='modalFooter'>
          <ModalFooterComponent onCloseModal={closeModal} isEditShow={false} />
        </div>
      </>
    </ModalComponent>
  );
};

export default ViewExerciseModal;

const TaxDetailsComponent = ({ viewData }: { viewData: any }): JSX.Element => {
  const taxDetails = viewData?.taxDetail;
  const totalTaxRate = taxDetails?.reduce(
    (total: number, item: any) => total + (item?.taxRate ?? 0),
    0
  );
  const totalTaxAmount = taxDetails?.reduce(
    (total: number, item: any) => total + (item?.taxAmount ?? 0),
    0
  );

  return (
    <div id='taxDetailsContent'>
      <Heading
        variant='h5'
        title='Tax Detail'
        color={COLORS.Gray}
        className='headingTitle'
        id='taxDetailsHeading'
      />
      {taxDetails && taxDetails.length > 0 ? (
        <div id='taxDetailsTable'>
          <TaxBracketAccordingValue id='taxBracketAccordingValue'>
            <TaxBracketHeaders className='grid grid-cols-12 gap-x-5' id='taxBracketHeaders'>
              <div className='col-span-3' id='taxNameHeader'>
                <Heading variant='body-xs' title='Tax Name' color={COLORS.Gray} />
              </div>
              <div className='col-span-4 flex' id='taxRateHeader'>
                <Heading variant='body-xs' title='Tax Rate (%)' color={COLORS.Gray} />
              </div>
              <div className='col-span-4 flex' id='taxValueHeader'>
                <Heading variant='body-xs' title='Value ($)' color={COLORS.Gray} />
              </div>
            </TaxBracketHeaders>

            <div id='taxBracketValues'>
              {taxDetails.map((item: any, index: number) => (
                <TaxBracketValue
                  className='grid lg:grid-cols-12 sm:grid-cols-1 items-start gap-x-5'
                  key={index}
                  id={`taxBracketRow.${index}`}>
                  <div className='col-span-3'>
                    <Heading variant='body-xs' title={item?.taxName || '-'} color={COLORS.Gray} />
                  </div>
                  <div className='col-span-4 flex'>
                    <Heading
                      variant='body-xs'
                      title={item?.taxRate ? String(item.taxRate) : '-'}
                      color={COLORS.Gray}
                    />
                  </div>
                  <div className='col-span-4 flex'>
                    <Heading
                      variant='body-xs'
                      title={
                        item?.taxAmount
                          ? String(
                              convertNumberWithCommas(
                                Number(item.taxAmount).toFixed(ACCEPT_DECIMAL),
                                true
                              )
                            )
                          : '-'
                      }
                      color={COLORS.Gray}
                    />
                  </div>
                </TaxBracketValue>
              ))}
            </div>
          </TaxBracketAccordingValue>
          <TotalContainer
            className='grid lg:grid-cols-12 sm:grid-cols-1 items-center gap-x-5'
            id='taxTotalContainer'>
            <div className='col-span-3'>
              <Heading variant='body-xs' title='Total Tax Amount' color={COLORS.Gray} />
            </div>
            <div className='col-span-4 flex pl-2'>
              <Heading
                variant='body'
                title={
                  totalTaxRate
                    ? convertNumberWithCommas(Number(totalTaxRate).toFixed(ACCEPT_DECIMAL), true) +
                      '%'
                    : '0%'
                }
                color={COLORS.Black}
              />
            </div>
            <div className='col-span-4 flex'>
              <Heading
                variant='body'
                title={
                  totalTaxAmount
                    ? '$' +
                      convertNumberWithCommas(Number(totalTaxAmount).toFixed(ACCEPT_DECIMAL), true)
                    : '$0'
                }
                color={COLORS.Black}
              />
            </div>
          </TotalContainer>
        </div>
      ) : (
        <div className='flex justify-center' id='noTaxDataMessage'>
          <Heading
            title={
              viewData?.transaction?.isExternalTransaction
                ? 'Applicable Tax not available for external transaction'
                : 'Applicable Tax not available'
            }
            variant='h6'
            color={COLORS.Purple}
          />
        </div>
      )}
    </div>
  );
};

const TransactionDetailsComponent = ({
  exerciseResponse,
}: {
  exerciseResponse: any;
}): JSX.Element => {
  const [transactionDetails, setTransactionDetails] = useState<any>();
  const data = exerciseResponse?.transaction;

  useEffect(() => {
    setTransactionDetails(data);
  }, [data]);

  return (
    <TransactionCard id='transactionCard'>
      <TransactionDetail id='transactionDetail'>
        <div id='transactionDetailTag'>
          <Tag title='Transaction Detail' variant='info' />
        </div>
        <div className='grid sm:grid-cols-2 lg:grid-cols-4 rowDetail' id='transactionDetailsGrid1'>
          <TransactionSingleDetailBlock id='transactionShares'>
            <div>
              <Heading variant='body-xs' title='Shares' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.shares === 0
                    ? '0'
                    : transactionDetails?.shares
                    ? String(
                        convertNumberWithCommas(
                          Number(transactionDetails.shares).toFixed(ACCEPT_DECIMAL),
                          false
                        )
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='fmvAtExercise'>
            <div>
              <Heading variant='body-xs' title='FMV at Exercise' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.fmv === 0
                    ? '$0'
                    : transactionDetails?.fmv
                    ? '$' +
                      convertNumberWithCommas(
                        Number(transactionDetails.fmv).toFixed(ACCEPT_DECIMAL),
                        true
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='exercisePrice'>
            <div>
              <Heading variant='body-xs' title='Exercise Price' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.totalCostPrice === 0
                    ? '$0'
                    : transactionDetails?.totalCostPrice
                    ? '$' +
                      convertNumberWithCommas(
                        Number(transactionDetails.totalCostPrice).toFixed(ACCEPT_DECIMAL),
                        true
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='incomeYtd'>
            <div>
              <Heading variant='body-xs' title='Income YTD' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.incomeYtd === 0
                    ? '$0'
                    : transactionDetails?.incomeYtd
                    ? '$' +
                      convertNumberWithCommas(
                        Number(transactionDetails.incomeYtd).toFixed(ACCEPT_DECIMAL),
                        true
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='incomeGain'>
            <div>
              <Heading variant='body-xs' title='Income Gain' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.incomeGain === 0
                    ? '$0'
                    : transactionDetails?.incomeGain
                    ? '$' +
                      convertNumberWithCommas(
                        Number(transactionDetails.incomeGain).toFixed(ACCEPT_DECIMAL),
                        true
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='totalCommission'>
            <div>
              <Heading variant='body-xs' title='Total Commission' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.totalCommision === 0
                    ? '$0'
                    : transactionDetails?.totalCommision
                    ? '$' +
                      convertNumberWithCommas(
                        Number(transactionDetails.totalCommision).toFixed(ACCEPT_DECIMAL),
                        true
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='totalFees'>
            <div>
              <Heading variant='body-xs' title='Total Fees' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.totalFees === 0
                    ? '$0'
                    : transactionDetails?.totalFees
                    ? '$' +
                      convertNumberWithCommas(
                        Number(transactionDetails.totalFees).toFixed(ACCEPT_DECIMAL),
                        true
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='totalTaxes'>
            <div>
              <Heading variant='body-xs' title='Total Taxes' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.totalTaxes === 0
                    ? '$0'
                    : transactionDetails?.totalTaxes
                    ? '$' +
                      convertNumberWithCommas(
                        Number(transactionDetails.totalTaxes).toFixed(ACCEPT_DECIMAL),
                        true
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='totalTaxesFeesCommissions'>
            <div>
              <Heading
                variant='body-xs'
                title='Total Taxes, Fees and Commissions'
                className='detailLabel'
              />
              <Heading
                variant='body'
                title={
                  transactionDetails?.totalTaxesAndFeesAndCommission === 0
                    ? '$0'
                    : transactionDetails?.totalTaxesAndFeesAndCommission
                    ? '$' +
                      convertNumberWithCommas(
                        Number(transactionDetails.totalTaxesAndFeesAndCommission).toFixed(2),
                        true
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='adjCostBasis'>
            <div>
              <Heading variant='body-xs' title='Adj Cost Basis' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.adjustedCostBasisPrice === 0
                    ? '$0'
                    : transactionDetails?.adjustedCostBasisPrice
                    ? '$' +
                      convertNumberWithCommas(
                        Number(transactionDetails.adjustedCostBasisPrice).toFixed(ACCEPT_DECIMAL),
                        true
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='fundingAmountRequired'>
            <div>
              <Heading variant='body-xs' title='Funding Amount Required' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.fundingAmountRequired === 0
                    ? '$0'
                    : transactionDetails?.fundingAmountRequired
                    ? '$' +
                      convertNumberWithCommas(
                        Number(transactionDetails.fundingAmountRequired).toFixed(ACCEPT_DECIMAL),
                        true
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='priorFundingAmount'>
            <div>
              <Heading variant='body-xs' title='Prior Funding Amount' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.priorFundingAmount === 0
                    ? '$0'
                    : transactionDetails?.priorFundingAmount
                    ? '$' +
                      convertNumberWithCommas(
                        Number(transactionDetails.priorFundingAmount).toFixed(ACCEPT_DECIMAL),
                        true
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='totalFundedAmount'>
            <div>
              <Heading variant='body-xs' title='Total Funded Amount' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.totalFundedAmount === 0
                    ? '$0'
                    : transactionDetails?.totalFundedAmount
                    ? '$' +
                      convertNumberWithCommas(
                        Number(transactionDetails.totalFundedAmount).toFixed(ACCEPT_DECIMAL),
                        true
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='currentStockPrice'>
            <div>
              <Heading variant='body-xs' title='Current Stock Price' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.currentStockPrice === 0
                    ? '$0'
                    : transactionDetails?.currentStockPrice
                    ? '$' +
                      convertNumberWithCommas(
                        Number(transactionDetails.currentStockPrice).toFixed(ACCEPT_DECIMAL),
                        true
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
        </div>
        <div className='grid sm:grid-cols-2 lg:grid-cols-4 rowDetail' id='transactionDetailsGrid2'>
          <TransactionSingleDetailBlock id='sellOrderShares'>
            <div>
              <Heading variant='body-xs' title='Sell Order Shares' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.sellOrderShares === 0
                    ? '0'
                    : transactionDetails?.sellOrderShares
                    ? String(
                        convertNumberWithCommas(
                          Number(transactionDetails.sellOrderShares).toFixed(ACCEPT_DECIMAL),
                          false
                        )
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='withholdSharesFromParticipant'>
            <div>
              <Heading
                variant='body-xs'
                title='Withhold Shares From Participant'
                className='detailLabel'
              />
              <Heading
                variant='body'
                title={
                  transactionDetails?.withheldSharesFromParticipant === 0
                    ? '0'
                    : transactionDetails?.withheldSharesFromParticipant
                    ? String(
                        convertNumberWithCommas(
                          Number(transactionDetails.withheldSharesFromParticipant).toFixed(
                            ACCEPT_DECIMAL
                          ),
                          false
                        )
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='transferSharesDueParticipant'>
            <div>
              <Heading
                variant='body-xs'
                title='Transfer Shares Due Participant'
                className='detailLabel'
              />
              <Heading
                variant='body'
                title={
                  transactionDetails?.transferSharesDueParticipant === 0
                    ? '0'
                    : transactionDetails?.transferSharesDueParticipant
                    ? String(
                        convertNumberWithCommas(
                          Number(transactionDetails.transferSharesDueParticipant).toFixed(
                            ACCEPT_DECIMAL
                          ),
                          false
                        )
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='netShare'>
            <div>
              <Heading variant='body-xs' title='Net Share' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.netShare === 0
                    ? '0'
                    : transactionDetails?.netShare
                    ? String(
                        convertNumberWithCommas(
                          Number(transactionDetails.netShare).toFixed(ACCEPT_DECIMAL),
                          false
                        )
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='moneyDueCompany'>
            <div>
              <Heading variant='body-xs' title='Money Due Company' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.moneyDueCompany === 0
                    ? '$0'
                    : transactionDetails?.moneyDueCompany
                    ? '$' +
                      convertNumberWithCommas(
                        Number(transactionDetails.moneyDueCompany).toFixed(ACCEPT_DECIMAL),
                        true
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='moneyDueParticipant'>
            <div>
              <Heading variant='body-xs' title='Money Due Participant' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.moneyDueParticipant === 0
                    ? '$0'
                    : transactionDetails?.moneyDueParticipant
                    ? '$' +
                      convertNumberWithCommas(
                        Number(transactionDetails.moneyDueParticipant).toFixed(ACCEPT_DECIMAL),
                        true
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='moneyDueWealthlane'>
            <div>
              <Heading variant='body-xs' title='Money Due Wealthlane' className='detailLabel' />
              <Heading
                variant='body'
                title={
                  transactionDetails?.moneyDueWealthlane === 0
                    ? '$0'
                    : transactionDetails?.moneyDueWealthlane
                    ? '$' +
                      convertNumberWithCommas(
                        Number(transactionDetails.moneyDueWealthlane).toFixed(ACCEPT_DECIMAL),
                        true
                      )
                    : '-'
                }
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
        </div>
        <div className='grid sm:grid-cols-2 lg:grid-cols-4 rowDetail' id='transactionDetailsGrid3'>
          <TransactionSingleDetailBlock id='isExternalTransaction'>
            <div>
              <Heading variant='body-xs' title='Is External Transaction' className='detailLabel' />
              <Heading
                variant='body'
                title={transactionDetails?.isExternalTransaction === true ? 'Yes' : 'No'}
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock id='comment'>
            <div>
              <Heading variant='body-xs' title='Comment' className='detailLabel' />
              <Heading
                variant='body'
                title={transactionDetails?.comment || '-'}
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
        </div>
        <div className='grid sm:grid-cols-2 lg:grid-cols-2 rowDetail'>
          <TransactionSingleDetailBlock>
            <div>
              <Heading variant='body-xs' title='Shares Destination' className='detailLabel' />
              <Heading
                variant='body'
                title={transactionDetails?.sharesDestination || '-'}
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
          <TransactionSingleDetailBlock>
            <div>
              <Heading variant='body-xs' title='Status Message' className='detailLabel' />
              <Heading
                variant='body'
                title={transactionDetails?.statusMessage || '-'}
                color={COLORS.Black}
              />
            </div>
          </TransactionSingleDetailBlock>
        </div>
      </TransactionDetail>
    </TransactionCard>
  );
};
